/**
 * Deterministic local-space motion for the Kraken's eight main arms.
 *
 * The mesh builder stores each arm's identity and lengthwise progress in UV2. The
 * renderer asks this pure function how far that slice has moved at a simulation time.
 * Suckers carry the same metadata, so they remain attached to the skin as the arm rolls.
 */

export interface KrakenArmOffset {
  x: number;
  y: number;
  z: number;
}

export interface KrakenEyeOffset {
  x: number;
  y: number;
  z: number;
}

interface ArmMotion {
  speed: number;
  travel: number;
  lateral: number;
  vertical: number;
  tipCurl: number;
  direction: number;
  phase: number;
}

const ARM_MOTION: readonly ArmMotion[] = [
  { speed: 0.12, travel: 4.8, lateral: 42, vertical: 18, tipCurl: 40, direction: 0.28, phase: 0.3 },
  { speed: 0.27, travel: 5.7, lateral: 22, vertical: 14, tipCurl: 25, direction: 1.12, phase: 2.1 },
  { speed: 0.17, travel: 4.2, lateral: 64, vertical: 12, tipCurl: 14, direction: -0.42, phase: 4.0 },
  { speed: 0.31, travel: 6.4, lateral: 20, vertical: 13, tipCurl: 22, direction: 2.22, phase: 1.1 },
  { speed: 0.21, travel: 5.1, lateral: 52, vertical: 12, tipCurl: 17, direction: 0.82, phase: 5.2 },
  { speed: 0.29, travel: 6.0, lateral: 26, vertical: 17, tipCurl: 30, direction: -1.36, phase: 3.2 },
  { speed: 0.09, travel: 4.5, lateral: 49, vertical: 14, tipCurl: 38, direction: 2.72, phase: 1.8 },
  { speed: 0.24, travel: 5.4, lateral: 44, vertical: 11, tipCurl: 22, direction: -2.38, phase: 4.7 },
];

function clamp01(value: number): number {
  return Math.max(0, Math.min(1, value));
}

function smoothstep(value: number): number {
  const t = clamp01(value);
  return t * t * (3 - 2 * t);
}

/**
 * A travelling displacement with a planted root and delayed tip.
 *
 * The phase subtracts `t * travel`: a contraction reaches the root first and takes time
 * to travel outward. Large low-frequency motion carries most of the mass; a smaller
 * second wave and tip curl prevent the silhouette from swinging as one rigid arc.
 */
export function krakenArmOffset(
  armIndex: number,
  progress: number,
  simTime: number,
  attacking: boolean,
): KrakenArmOffset {
  const motion = ARM_MOTION[armIndex];
  if (!motion) return { x: 0, y: 0, z: 0 };

  const t = clamp01(progress);
  const planted = t * t * (3 - 2 * t);
  if (planted === 0) return { x: 0, y: 0, z: 0 };

  const phase = simTime * motion.speed - t * motion.travel + motion.phase;
  const primary = Math.sin(phase);
  const secondary = Math.sin(
    simTime * motion.speed * 0.63 - t * motion.travel * 1.47 + motion.phase * 1.71,
  );
  const middle = Math.sin(Math.PI * t);
  const tip = Math.pow(t, 2.65);
  const attackScale = attacking ? 1.24 : 1;

  const lateral = (
    primary * motion.lateral * (0.24 + t * 0.76)
    + secondary * motion.lateral * 0.24 * middle
  ) * planted * attackScale;
  const lift = (
    Math.sin(phase * 0.73 + motion.phase * 0.31)
      * motion.vertical * (0.18 + t * 0.82)
    + Math.sin(simTime * 0.13 + motion.phase) * motion.tipCurl * tip
    + (attacking ? Math.sin(simTime * 0.38 + motion.phase * 0.7) * motion.tipCurl * tip * 0.42 : 0)
  ) * planted;

  const directionX = Math.cos(motion.direction);
  const directionZ = Math.sin(motion.direction);
  const crossX = -directionZ;
  const crossZ = directionX;

  return {
    x: directionX * lateral + crossX * secondary * motion.lateral * 0.08 * planted,
    y: lift,
    z: directionZ * lateral + crossZ * secondary * motion.lateral * 0.08 * planted,
  };
}

/**
 * The stare and the death, overriding the eye's ordinary life.
 *
 * `hold` pins the lids open — no blink survives it, because a creature staring the
 * player down does not blink. `rollBack` is the coup de grâce landing: the pupil climbs
 * up into the head and the whole wet globe withdraws into its socket, over the seconds
 * the fall takes to begin.
 */
export interface KrakenEyeState {
  hold: number;
  rollBack: number;
}

/**
 * Slow eye tracking plus a short, muscular blink.
 *
 * Role 1 is the pupil/glint and role 4 is the globe. The pupil tracks slowly while the
 * whole wet surface contracts into its deep socket for one short beat in an eleven-second
 * cycle. That reads as cephalopod tissue withdrawing rather than two modelled pieces
 * hinged over the eye.
 */
export function krakenEyeOffset(
  role: number,
  simTime: number,
  state?: KrakenEyeState,
): KrakenEyeOffset {
  const hold = state ? clamp01(state.hold) : 0;
  const rollBack = state ? smoothstep(state.rollBack) : 0;
  const lookY = Math.sin(simTime * 0.083 + 0.7) * 3.8 * (1 - hold);
  const lookZ = Math.sin(simTime * 0.061 + 2.1) * 7.5 * (1 - hold);
  const cycle = ((simTime + 1.9) % 11.3 + 11.3) % 11.3;
  const distanceFromBlink = Math.abs(cycle - 5.65);
  const blink = smoothstep((0.64 - distanceFromBlink) / 0.64) * (1 - hold);

  if (role === 1) {
    // The pupil: blink-withdrawn in life; at death it climbs and sinks away for good.
    return {
      x: -blink * 74 - rollBack * 82,
      y: lookY + rollBack * 34,
      z: lookZ,
    };
  }
  if (role === 4) return { x: -blink * 70 - rollBack * 30, y: 0, z: 0 };
  return { x: 0, y: 0, z: 0 };
}
